import { FormStatus } from '../../../../../types/form-state';
import React, { useEffect } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Divider } from 'wix-ui-tpa/cssVars';
import { ExpressCheckoutButtonsState } from '@wix/ecom-platform-express-checkout-buttons';
import { ExpressCheckoutButtons } from '@wix/ecom-platform-express-checkout-buttons/widget';
import {
  ExpressCheckoutInfo,
  ServiceData,
  SlotService,
  WixFormsFieldProperties,
} from '../../../../../utils/state/types';
import { mapToArray } from '../../../../../utils';
import {
  areAllFormsFilled,
  ExpressCheckoutMode,
  hasPayLater,
} from '../../../../../utils/expressCheckout/expressCheckoutEligibility';
import { useFormActions } from '../../../Hooks/useFormActions';
import { useFormComponentContext } from '../../../Hooks/useFormComponentContext';
import Text, { TextType } from '../../../Text/Text';
import { classes } from './ExpressCheckout.st.css';
import { ButtonAdapterDataHooks } from '../dataHooks';

const DEMO_ESTIMATED_TOTAL = 10;

export interface ExpressCheckoutProps {
  serviceData: ServiceData;
  expressCheckout?: ExpressCheckoutInfo;
  fieldProperties?: WixFormsFieldProperties;
  status: FormStatus;
}

export const ExpressCheckout: React.FC<ExpressCheckoutProps> = ({
  serviceData,
  expressCheckout,
  fieldProperties,
  status,
}) => {
  const { t } = useTranslation();
  const {
    validateWixForm,
    onExpressCheckoutFormReady,
    onExpressCheckoutWouldShow,
  } = useFormActions();
  const { wixFormValues, participantFormValues, bookerFormValues } =
    useFormComponentContext();
  const validationRequestId = expressCheckout?.validationRequestId;
  const { payNow, totalPrice } = serviceData.summaryPaymentDetails;
  const isDemo = expressCheckout?.mode === ExpressCheckoutMode.Demo;
  const estimatedTotal =
    (payNow ?? totalPrice) || (isDemo ? DEMO_ESTIMATED_TOTAL : 0);
  const hasWallets =
    expressCheckout?.buttonsState ===
    ExpressCheckoutButtonsState.LOADED_WITH_BUTTONS;

  const wouldShow = hasWallets && !isDemo && estimatedTotal > 0;

  useEffect(() => {
    if (wouldShow) {
      onExpressCheckoutWouldShow();
    }
  }, [wouldShow]);

  useEffect(() => {
    if (!validationRequestId) {
      return;
    }
    let isActive = true;
    void validateWixForm()
      .catch(() => false)
      .then((isValid) => {
        if (isActive) {
          onExpressCheckoutFormReady({
            isValid,
            wixFormValues,
            participantFormValues,
            bookerFormValues,
            validationRequestId,
          });
        }
      });
    return () => {
      isActive = false;
      onExpressCheckoutFormReady({
        isValid: false,
        validationRequestId,
        wixFormValues,
      });
    };
  }, [validationRequestId]);

  const hasNoButtons =
    expressCheckout?.buttonsState ===
    ExpressCheckoutButtonsState.LOADED_WITHOUT_BUTTONS;

  const currency =
    mapToArray<SlotService>(serviceData.slotServices)[0]?.paymentDetails
      ?.currency ?? undefined;
  const canStartPayment =
    isDemo ||
    (expressCheckout?.mode === ExpressCheckoutMode.Live &&
      status === FormStatus.IDLE &&
      areAllFormsFilled({
        fieldProperties,
        wixFormValues,
        participantFormValues,
        bookerFormValues,
      }));

  return (
    <div
      className={classes.root}
      hidden={
        hasNoButtons || expressCheckout?.mode === ExpressCheckoutMode.Control
      }
    >
      <div className={classes.divider}>
        <Divider className={classes.line} />
        <Text type={TextType.Secondary}>
          {t('app.bookings-form.express-checkout.title')}
        </Text>
        <Divider className={classes.line} />
      </div>
      <ExpressCheckoutButtons
        dataHook={ButtonAdapterDataHooks.EXPRESS_CHECKOUT}
        isShippable={false}
        hasPayLater={!isDemo && hasPayLater(serviceData.summaryPaymentDetails)}
        canStartPayment={canStartPayment}
        currency={currency}
        estimatedTotal={String(estimatedTotal)}
        withLoader
      />
    </div>
  );
};
